import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCameraRequestVideoMutation,
  useGetAssignedDriverQuery,
  useGetEventCameraRecordingQuery,
  useGetVehicleCameraDetailsQuery,
  useSingleOrganizationVehicleQuery,
  useVehicleRecorderStatusQuery,
  AdminAPIs,
  AdminApiTags,
} from "../../api/network/adminApiServices";
import {
  useEventGetVideoDetailsMutation,
  useGetCameraConfigQuery,
  useGetCameraDetailsQuery,
  useGetDeviceStorageListingQuery,
} from "../../api/network/vmApiService";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import {
  BuzzerAlert,
  BuzzerAlertStatuses,
} from "../../api/types/Notifications";
import { TLegacyVehicle } from "../../api/types/Vehicle";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import svgIcons from "../../assets/svg/svgIcons";
import {
  AdminFormFieldDropdown,
  AdminFormFieldSubmit,
} from "../../components/admin/formFields";
import { APP_CONFIG } from "../../constants/constants";
import useBuzzerAlertActions from "../../hooks/buzzerAlertActions";
import { routeUrls } from "../../navigation/routeUrls";
import { TMapType } from "../../types/map";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../utils/dateTime";
import { convertKmphTo } from "../../utils/distance";
import { roundOffLatLong } from "../../utils/map";
import { getBuzzerAlertUUID } from "../../utils/notification";
import { useLoggedInUserData } from "../../utils/user";
import {
  consistentDisplayAssetItem,
  getVehicleEventStatusDisplayName,
} from "../../utils/vehicle";
import {
  ACCOUNT_RECORDER_TYPE_VALUES,
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
} from "../admin-details/account/constants";
import ModalMaintenanceFaultCodes from "../admin-details/maintenance/components/modalMaintenanceFaultCodes";
import {
  mapVehicleDisplayIds,
  mapVehicleDisplayTitle,
} from "../map-fleet/common";
import ModalSendLocation from "../map-fleet/modalSendLocation";
import ModalSendText from "../map-fleet/modalSendText";
import WeatherForecastModal from "../map-fleet/modalWeatherForecast";
import ModalCameraVideo from "./modalCameraVideo";
import { CameraEventGetVideoDetailResponseVideos } from "../../api/types/Camera";
import {
  useGetDeviceListingQuery,
  useRequestVideoMutation,
} from "../../api/network/camerasAdminVMApiService";
import {
  CAMERA_CAPTURE_MODES,
  RECORDING_TYPE,
  ALL_CAMERA_CLIP_TYPES,
  CAMERA_ID_TYPES,
} from "../camera-admin/constants";
import ModalCameraPlayback from "./modalCameraPlayback";

type TEventSpec = {
  name: string;
  value: ReactNode | string | number;
  sub?: string | null;
};

type ModalVehicleTripEventDetailsProps = {
  vehicleId?: any;
  vehicleData: TLegacyVehicle;
  tripPointData: VehicleMapDataPoint | null;
  setSelectedTripPoint?: (tripPoint: VehicleMapDataPoint | null) => void;
  handleMapTypeChange?: (
    mapType: TMapType,
    latLng?: google.maps.LatLng
  ) => void;
  buzzerAlertTimestamp?: string;
  buzzerAlertId?: number;
  onEventDetailModalClose?: () => void;
};

const debugComponent = false;

const ModalVehicleTripEventDetails: FC<ModalVehicleTripEventDetailsProps> = ({
  vehicleId,
  vehicleData,
  tripPointData,
  setSelectedTripPoint,
  handleMapTypeChange,
  buzzerAlertTimestamp,
  buzzerAlertId,
  onEventDetailModalClose,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "mapFleet.details",
  });
  const { t: tBuzzerAlert } = useTranslation("translation", {
    keyPrefix: "buzzerAlerts.actions",
  });
  const { t: tCameras } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const accountData = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const eventData = tripPointData;

  const [listData, setListData] = useState<TEventSpec[]>([]);
  // video allowed at account level
  const thisOrgViewVideoAllowed = useLoggedInUserData("viewVideoAllowed");
  const thisOrgCameraRecorderType = useLoggedInUserData("cameraRecorderType");
  // fetch single vehicle details
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const [selectedClipTypes, setSelectedClipTypes] = useState<string[]>([
    CAMERA_CAPTURE_MODES.front,
  ]);
  const [clipOptions, setClipOptions] = useState(ALL_CAMERA_CLIP_TYPES);

  // fetch single driver details
  const {
    data: dataAssignedDriver,
    isLoading: isLoadingAssignedDriver,
    isError: isErrorAssignedDriver,
  } = useGetAssignedDriverQuery(
    {
      account_id: thisUserOrganizationId ?? "",
      device_id: vehicleId?.length > 0 ? vehicleId : "",
    },
    { skip: !vehicleId }
  );
  // Fetch vehicle camera details
  const { data: vehicleCameraDetails } = useGetVehicleCameraDetailsQuery(
    {
      account_id: thisUserOrganizationId ?? "",
      device_id: vehicleId?.length > 0 ? vehicleId : "",
    },
    { skip: !vehicleId }
  );
  // Fetch Camera recording for event
  const {
    currentData: dataRecordingDetails,
    isFetching: isFetchingRecordingData,
  } = useGetEventCameraRecordingQuery(
    {
      device_id: vehicleId?.length > 0 ? vehicleId : "",
      timestamp: eventData?.timestamp || "",
      event_id: eventData?.statuscode || "",
      // timestamp: "1668639357",
      // event_id: "63809",
    },
    {
      skip:
        !vehicleCameraDetails ||
        !vehicleCameraDetails?.is_recording_supported ||
        (thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX
          ? false // if visionmax, ignore the eventData params, i.e. make the API call irrespective
          : !eventData?.timestamp || !eventData?.statuscode),
    }
  );

  const [
    vMCameraEventGetVideoDetailResponse,
    setVMCameraEventGetVideoDetailResponse,
  ] = useState<CameraEventGetVideoDetailResponseVideos | null>(null);

  const [
    eventGetVideoDetailsAPITrigger,
    {
      isLoading: isLoadingVideoDetails,
      isUninitialized: isUninitializedVideoDetails,
    },
  ] = useEventGetVideoDetailsMutation();

  // if (
  //   !vehicleCameraDetails ||
  //   !vehicleCameraDetails?.is_recording_supported ||
  //   !eventData?.timestamp ||
  //   !eventData?.statuscode
  // ) {
  //   if (APP_CONFIG.DEBUG.ALL)
  //     console.log(
  //       "== API SKIPPED",
  //       !vehicleCameraDetails,
  //       !vehicleCameraDetails?.is_recording_supported,
  //       !eventData?.timestamp,
  //       !eventData?.statuscode,
  //       eventData,
  //       tripPointData
  //     );
  // } else {
  //   if (APP_CONFIG.DEBUG.ALL) console.log("++ API TRIGGERED");
  // }

  const orgDistanceUnit = useLoggedInUserData(
    "speedUnits"
  ) as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgDistanceUnit)
      ?.unit || SPEED_UNITS_OPTIONS[0].unit;

  useEffect(() => {
    if (tripPointData) {
      setListData([
        {
          name: "Current Ignition Status",
          value:
            eventData?.ignition_state_descr?.toUpperCase() /* || tripPointData?.ignitionindex */ ||
            "Not Available",
        },
        {
          name: "Date/Time",
          value: eventData?.timestamp
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(parseInt(`${eventData?.timestamp}`)),
                accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                "LLL dd, yyyy HH:mm:ss"
              ) + ` (${accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE})`
            : "-",
        },
        // {
        //   name: "GPS",
        //   value: eventData?.gpsage || "Not Available"
        // },
        {
          name: "Speed",
          value: !!eventData?.speedkph
            ? Math.round(convertKmphTo(speedUnit, eventData?.speedkph))
            : // ?.toFixed(2)
              // .replace(".00", "")
              APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS[
                  `${eventData?.ignitionstate}` as keyof typeof APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS
                ] === "ON"
              ? "0.0"
              : "Not Available",
          sub:
            !!eventData?.speedkph ||
            APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS[
              `${eventData?.ignitionstate}` as keyof typeof APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS
            ] === "ON"
              ? `in ${
                  SPEED_UNITS_OPTIONS.find(
                    (option) => option.unit === speedUnit
                  )?.label || SPEED_UNITS_OPTIONS[0].label
                }`
              : null,
        },
        {
          name: "Speed Limit",
          value: !!eventData?.speedlimitkph
            ? Math.round(convertKmphTo(speedUnit, eventData?.speedlimitkph))
            : // ?.toFixed(2)
              // .replace(".00", "")
              "Not Available",
          sub: !!eventData?.speedlimitkph
            ? `in ${
                SPEED_UNITS_OPTIONS.find((option) => option.unit === speedUnit)
                  ?.label || SPEED_UNITS_OPTIONS[0].label
              }`
            : null,
        },
        {
          name: "Fault Codes",
          value: !!vehicleData?.fault_code_info ? (
            <span
              className="text-blue-500 underline cursor-pointer"
              onClick={() => {
                // Handle link click, show fault codes modal
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showModalMaintenanceFaultCodes: true,
                  })
                );
              }}
            >
              {Object.keys(vehicleData?.fault_code_info).join("; ")}
            </span>
          ) : (
            "No DTC's available"
          ),
        },
        {
          name: "Status",
          value: (
            <span className="font-bold uppercase">
              {getVehicleEventStatusDisplayName(tripPointData) ||
                "Not Available"}
              {/* {mapVehicleOrientation(tripPointData) > 0
              || mapVehicleIsActive(tripPointData)
              ? <span className="text-field-success">{tMain('active')}</span>
              : <span className="text-field-error-dark">{tMain('inactive')}</span>} */}
            </span>
          ),
        },
        {
          name: "Latitude/Longitude",
          value:
            eventData?.latitude === 0 && eventData?.longitude === 0
              ? "0, 0"
              : eventData?.latitude
                ? `${roundOffLatLong(eventData?.latitude)}, ${roundOffLatLong(eventData?.longitude)}`
                : "Not Available",
        },
        {
          name: "Address",
          value: eventData?.address || "Not Available",
        },
        ...(showDriver
          ? [
              {
                name: "Driver",
                value:
                  // error in response implies that driver is not assigned
                  !vehicleId ||
                  !!isErrorAssignedDriver ||
                  !dataAssignedDriver?.driverid
                    ? "Not Available"
                    : [
                        // dataAssignedDriver?.displayname,
                        dataAssignedDriver?.description,
                        dataAssignedDriver?.driverid,
                      ]
                        .filter((item) => !!item)
                        .join(" | ") || "Not Available",
              },
            ]
          : []),
        {
          name: "Driver Phone",
          value:
            // error in response implies that driver is not assigned
            !vehicleId ||
            !!isErrorAssignedDriver ||
            !dataAssignedDriver?.driverid
              ? "Not Available"
              : dataAssignedDriver?.contactphone || "Not Available",
        },
        {
          name: "First Ignition",
          value: eventData?.first_ignition
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(parseInt(`${eventData?.first_ignition}`)),
                accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                "LLL dd, yyyy HH:mm:ss"
              ) + ` (${accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE})`
            : "Not Available",
        },
        {
          name: "Groups",
          value:
            vehicleData?.groups?.map((group) => group.groupid).join(", ") ||
            "Not Available",
        },
        {
          name: "Last Valid Event",
          value: vehicleData?.lasteventtimestamp
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(parseInt(`${vehicleData?.lasteventtimestamp}`)),
                accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                "LLL dd, yyyy HH:mm:ss"
              ) + ` (${accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE})`
            : "Not Available",
        },
        {
          name: "Has Impact",
          value: "Not Available", // TODO: need to discuss
        },
        {
          name: "Has FleetTag",
          value: eventData?.rfidtag || "Not Available",
        },
      ]);
    }
  }, [
    vehicleId,
    tripPointData,
    dataAssignedDriver,
    isErrorAssignedDriver,
    vehicleData,
  ]);

  // request video mutation
  const [requestVideo, { isLoading: isLoadingCameraRequestVideo }] =
    useCameraRequestVideoMutation();

  // visionmax request video mutation
  const [requestVideoVM, { isLoading: isLoadingCameraRequestVideoVM }] =
    useRequestVideoMutation();

  // visionmax - check if device is online
  const { data: dataDeviceListing, isFetching: isFetchingDeviceListing } =
    useGetDeviceListingQuery(
      {
        keyword: vehicleData?.cameraid, // deviceEventFromState.cameraid,
        model: "",
        status: "",
        offset: 0,
        limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      },
      {
        skip:
          thisOrgCameraRecorderType !== ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX,
        refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
      }
    );

  // Get Device config
  const { data: cameraConfigResponse } = useGetCameraConfigQuery(
    vehicleData.cameraid
  ); // (cameraId);
  const cameraConfig = cameraConfigResponse?.data ?? null;
  const { data: deviceDetailRespsonse, isLoading: isLoadingDeviceData } =
    useGetCameraDetailsQuery(
      dataDeviceListing?.data?.[0]?.cid ?? "", // cameraId here was actually the serial number
      {
        skip: !dataDeviceListing?.data?.[0]?.cid, // cameraId
      }
    );
  const dataDeviceDetails =
    deviceDetailRespsonse?.data && deviceDetailRespsonse?.data?.length > 0
      ? deviceDetailRespsonse?.data[0]
      : null;

  const { data: deviceStorageData } = useGetDeviceStorageListingQuery(
    {
      cid: dataDeviceListing?.data?.[0]?.cid ?? "",
      start_timestamp: Math.floor(Date.now() / 1000) - (30 * 24 * 60 * 60),
      end_timestamp: Math.floor(Date.now() / 1000), // Now
    },
    { 
      skip: !dataDeviceListing?.data?.[0]?.cid,
    }
  );
  /**
   * Recording type of camera
   * If recording type is Separate, then individual camera clip can be requested
   * If recording type is Side-By-Side, then individual camera calip cannot be requested (ALL should be requested)
   */
  const recordingType = useMemo(() => {
    if (cameraConfig?.current && cameraConfig?.current?.device) {
      const currentDeviceConfig = cameraConfig.current.device;
      const skuName = dataDeviceDetails?.sku_name?.replace("C", ""); // Remove C at the end to match config key
      const key =
        `vr_${skuName}_embedded_videoFormat` as keyof typeof currentDeviceConfig;
      const recordingType =
        key in currentDeviceConfig ? currentDeviceConfig[key] : undefined;
      return recordingType as RECORDING_TYPE;
    }
    return undefined;
  }, [dataDeviceDetails, cameraConfig]);

  useEffect(() => {
    const config = (cameraConfig?.current?.device || {}) as any;
    const sku = dataDeviceDetails?.sku_name?.replace("C", "");
    const getOption = (val: string) =>
      ALL_CAMERA_CLIP_TYPES.find((o) => o.value === val);

    const keyEmbedded = `vr_${sku}_embedded_videoFormat`;
    const keyUVC = `vr_${sku}_uvc_videoFormat`;
    const key4TVI = `vr_${sku}_4tvi_videoFormat`;

    const isSideBySide = config[keyEmbedded] === RECORDING_TYPE.sideBySide;
    
    // Get available capture modes from SD card storage
    const availableCaptureModes = (Array.isArray(deviceStorageData?.data)
    ? deviceStorageData?.data.map((storage) => storage.capture_mode)
    : []) || [];
    
    // Determine camera availability
    // If storage data exists and has modes, use it; otherwise fall back to config
    const hasStorageData = availableCaptureModes.length > 0;
    
    const hasSideBySide = hasStorageData 
      ? availableCaptureModes.includes('S_F_I')
      : isSideBySide;
      
    const hasFront = hasStorageData
      ? availableCaptureModes.includes('C_F')
      : !isSideBySide; // If no storage data, assume front exists for non-SBS
      
    const hasInCabin = hasStorageData
      ? availableCaptureModes.includes('C_I')
      : !isSideBySide; // If no storage data, assume in-cabin exists for non-SBS
      
    const hasAuxUSB = hasStorageData
      ? availableCaptureModes.includes('C_R')
      : false; // Conservative: only show if we have proof from storage
      
    const hasTVIHardware = hasStorageData
      ? availableCaptureModes.includes('C_E')
      : (config[key4TVI] != null && config[key4TVI] !== "" && config[key4TVI] !== false);
      
    const supportsGrid = hasStorageData
      ? availableCaptureModes.includes('GRID')
      : config[key4TVI] === "Grid";

    const viewList = [
      ...(hasSideBySide ? [getOption(CAMERA_CAPTURE_MODES.combined)] : []),
      ...(hasFront && !isSideBySide ? [getOption(CAMERA_CAPTURE_MODES.front)] : []),
      ...(hasInCabin && !isSideBySide ? [getOption(CAMERA_CAPTURE_MODES.in_cabin)] : []),
      ...(hasAuxUSB ? [getOption(CAMERA_ID_TYPES.rear)] : []),
      ...(hasTVIHardware ? [getOption(CAMERA_ID_TYPES.extended)] : []),
      ...(supportsGrid ? [getOption(CAMERA_CAPTURE_MODES.grid)] : []),
    ].filter((item): item is (typeof ALL_CAMERA_CLIP_TYPES)[number] => !!item);

    setClipOptions(viewList);

    if (viewList.length > 0) {
      const validSelections = selectedClipTypes.filter((id) =>
        viewList.some((v) => v.value === id)
      );

      if (validSelections.length === 0) {
        const defaultOpt =
          viewList.find((o) => o.value === CAMERA_CAPTURE_MODES.combined) ||
          viewList[0];
        setSelectedClipTypes([defaultOpt.value]);
      } else {
        setSelectedClipTypes(validSelections);
      }
    }
  }, [cameraConfig, dataDeviceDetails, recordingType, deviceStorageData]);

  const requestVisionmaxVideo = async () => {
    toast.info(
      `Requesting ${selectedClipTypes.length} video(s) from the device. This may take a couple of minutes. Please do not close the modal or navigate to another page.`
    );

    let successCount = 0;
    let failCount = 0;

    // Loop through each selected type one by one
    for (const type of selectedClipTypes) {
      try {
        // Send Request
        await requestVideoVM({
          timestamp: eventData?.timestamp as number,
          serial_number: vehicleData.cameraid,
          duration_in_seconds:
            APP_CONFIG.VISIONMAX.REQUEST_VIDEO.DEFAULT_CLIP_DURATION_SEC,
          cam_id: type, // Send specific ID
        }).unwrap();

        // Success
        successCount++;

        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Error requesting view ${type}:`, error);
        failCount++;
      }
    }

    // Final Report
    if (successCount > 0) {
      toast.success(
        `${successCount} file(s) requested successfully. Your file will be available in File Retrievals once it is ready.`
      );

      dispatch(
        AdminAPIs.util.invalidateTags([AdminApiTags.ASSET_EVENT_VIDEO_RECORDING])
      );
    }
    if (failCount > 0) {
      toast.error(`${failCount} request(s) failed.`);
    }
  };

  const getVideoDetails = useCallback(() => {
    // use ticket reference id to fetch video details
    if (dataRecordingDetails?.recording?.video_data?.[0].referenceid) {
      eventGetVideoDetailsAPITrigger({
        ticketId:
          dataRecordingDetails?.recording?.video_data?.[0].referenceid || "",
      })
        .unwrap()
        .then((response) => {
          if (response.data?.videos?.length === 0) {
            toast.error("No video details found");
            return;
          }
          // Set video details response
          setVMCameraEventGetVideoDetailResponse(response.data);
          // Show video modal with fetched details
          dispatch(
            setModalsData({
              ...modalsState,
              showVehicleCameraVideo: true,
            })
          );
        })
        .catch((error) => {
          toast.error("Failed to fetch video details");
        });
    } else {
      toast.error("No video reference ID found");
    }
  }, [
    dataRecordingDetails,
    eventGetVideoDetailsAPITrigger,
    dispatch,
    modalsState,
  ]);

  const AI_EVENT_TYPES = [
    63593, // Sleep
    63592, // Fatigue
    63595, // Distraction
    63936, // FCWS - Frontal Collision Warning
    63535, // Tailgating
    63857, // Lane Drift / Lane Departure
    63862, // Stop and Go
    63588, // Cell Phone Use
    //35, Yawning - NO STATUS CODE FOR THIS
    62698, // Unfastened seatbelt
    61814, // Speed Camera
    63858, // Rolling Stop
    63587, // Smoking
    63596, // Eating and Drinking
  ];

  const isAIEvent = (statusCode?: string | number): boolean => {
    if (!statusCode) return false;
    const code = typeof statusCode === 'string' ? parseInt(statusCode) : statusCode;
    return AI_EVENT_TYPES.includes(code);
  };

  // Button text and disabled state based on video request status
  const requestVideoButtonState = useMemo(() => {
    const currentEventIsAI = isAIEvent(eventData?.statuscode);
    
    // logic here for visionmax
    if (thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX) {
      
      // Check if this is an AI event (automatically requested)
      if (currentEventIsAI) {
        // AI events have video automatically requested
        if (dataRecordingDetails?.recording?.status === "View Video") {
          return {
            label: "View Video",
            disabled: false,
            onClick: getVideoDetails,
          };
        } else {
          // Video not yet available for AI event
          return {
            label: "Video Requested",
            disabled: true,
            onClick: () => {},
          };
        }
      }
      
      // GPS Events - manual request flow
      if (dataRecordingDetails?.recording?.status === "Request Video") {
        if (
          (dataDeviceListing?.data?.[0]?.is_online === "offline" ||
            dataDeviceListing?.data?.[0]?.is_online === "standby") &&
          !!dataDeviceListing?.data?.[0]?.ft_device_id
        ) {
          // Device offline/standby - can request via wakeup
          return {
            label: "Request Video",
            disabled: false,
            onClick: () => {
              dispatch(
                setModalsData({
                  ...modalsState,
                  showModalWakeupCamera: {
                    cid: dataDeviceListing?.data?.[0]?.cid || "",
                    serial_number: vehicleData.cameraid,
                    onSuccessCallback: requestVisionmaxVideo,
                  },
                })
              );
            },
          };
        } else if (
          dataDeviceListing?.data?.[0]?.is_online === "online" &&
          !!dataDeviceListing?.data?.[0]?.ft_device_id
        ) {
          // Device online - can request directly
          return {
            label: "Request Video",
            disabled: false,
            onClick: requestVisionmaxVideo,
          };
        }
        // Device status unknown or unavailable
        return {
          label: "No Video Available",
          disabled: true,
          onClick: () => {},
        };
      } else if (dataRecordingDetails?.recording?.status === "View Video") {
        // Video is available
        return {
          label: "View Video",
          disabled: false,
          onClick: getVideoDetails,
        };
      } else if (dataRecordingDetails?.recording?.status === "Video Already Requested") {
        // Video already requested (by user) - FIXED STATUS STRING
        return {
          label: "Video Requested", // Simplified label for UI
          disabled: true,
          onClick: () => {},
        };
      } else {
        // Video request in progress or other unknown status
        return {
          label: dataRecordingDetails?.recording?.status || "No Video Available",
          disabled: true,
          onClick: () => {},
        };
      }
    }
    
    // surfsight cases below
    else if (
      !vehicleCameraDetails ||
      !vehicleCameraDetails?.is_recording_supported
    ) {
      // Video not supported
      return {
        label: "No Video Available",
        disabled: true,
        onClick: () => {},
      };
    } else if (dataRecordingDetails) {
      // Surfsight - check AI event first
      if (currentEventIsAI) {
        if (dataRecordingDetails?.recording?.status === "View Video") {
          return {
            label: "View Video",
            disabled: false,
            onClick: () => {
              dispatch(
                setModalsData({ ...modalsState, showVehicleCameraVideo: true })
              );
            },
          };
        } else {
          return {
            label: "Video Requested",
            disabled: true,
            onClick: () => {},
          };
        }
      }
      
      // GPS events - manual request
      if (dataRecordingDetails?.recording?.status === "View Video") {
        return {
          label: "View Video",
          disabled: false,
          onClick: () => {
            dispatch(
              setModalsData({ ...modalsState, showVehicleCameraVideo: true })
            );
          },
        };
      } else if (dataRecordingDetails?.recording?.status === "Request Video") {
        return {
          label: "Request Video",
          disabled: false,
          onClick: () => {
            if (vehicleId) {
              requestVideo({
                device_id: vehicleId?.length > 0 ? vehicleId : "",
                timestamp: eventData?.timestamp || "",
                event_id: eventData?.statuscode || "",
              })
                .unwrap()
                .then(() => {
                  toast.success("Video request sent successfully");
                })
                .catch((error) => {
                  toast.error("Failed to request video");
                });
            } else {
              toast.error("Asset ID not found");
            }
          },
        };
      } else if (dataRecordingDetails?.recording?.status === "Video Already Requested") {
        // FIXED STATUS STRING
        return {
          label: "Video Requested",
          disabled: true,
          onClick: () => {},
        };
      } else {
        return {
          label: "No Video Available",
          disabled: true,
          onClick: () => {},
        };
      }
    }
    
    // Default state
    return {
      label: "Request Video",
      disabled: true,
      onClick: () => {},
    };
  }, [
    vehicleCameraDetails,
    dataRecordingDetails,
    dataDeviceListing,
    eventData,
    recordingType,
    selectedClipTypes,
    getVideoDetails,
    dispatch,
    modalsState,
    requestVisionmaxVideo,
    requestVideo,
    vehicleId,
    vehicleData,
    thisOrgCameraRecorderType,
  ]);

  // for playing video directly from SD card - check camera recorder status
  const { data: dataSingleVehicle, isFetching: isFetchingSingleVehicle } =
    useSingleOrganizationVehicleQuery(
      {
        account_id: thisUserOrganizationId,
        device_id: vehicleId?.length > 0 ? vehicleId : "",
      },
      {
        skip:
          thisOrgViewVideoAllowed !== "1" ||
          thisOrgCameraRecorderType !==
            ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX ||
          !vehicleId,
      }
    );
  const {
    data: dataVehicleRecorderStatus,
    isFetching: isFetchingVehicleRecorderStatus,
    refetch: refetchVehicleRecorderStatus,
  } = useVehicleRecorderStatusQuery(
    {
      account_id: thisUserOrganizationId ?? "",
      device_id: vehicleId?.length > 0 ? vehicleId : "",
    },
    {
      skip:
        thisOrgViewVideoAllowed !== "1" ||
        thisOrgCameraRecorderType !== ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX ||
        !vehicleId ||
        isFetchingSingleVehicle ||
        !dataSingleVehicle ||
        dataSingleVehicle?.is_recording_supported !== true,
      refetchOnMountOrArgChange: true, // caching removed to ensure status always shows correct status
    }
  );

  const onCameraAwakeAndReady = useCallback(() => {
    if (!eventData?.timestamp) {
      if (APP_CONFIG.DEBUG.ALL)
        console.log("Event timestamp not found", eventData);
      return;
    }
    dispatch(
      setModalsData({
        ...modalsState,
        showModalVideoPlayback: {
          startTimestamp: eventData?.timestamp as number,
          duration: 30, // Default 30 sec. for playback
          captureMode: selectedClipTypes[0],
          // recordingType === RECORDING_TYPE.sideBySide
          //   ? CAMERA_CAPTURE_MODES.combined
          //   : CAMERA_CAPTURE_MODES.front,
          action: "play", // Indicate action for playback
        },
      })
    );
  }, [modalsState, eventData, selectedClipTypes, dispatch]);

  // visionmax - wakeup camera success callback
  const onCameraWakeupSuccess = useCallback(
    (serialNumber: string) => {
      refetchVehicleRecorderStatus();

      setTimeout(() => {
        dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: true }));
        toast.info(
          "Allowing a few seconds for the camera to wake up and connect with servers..."
        );
        setTimeout(() => {
          dispatch(
            setModalsData({ ...modalsState, showPrimaryOverlay: false })
          );
          // Initiate Camera live view for currently selected camera
          if (!!serialNumber) {
            onCameraAwakeAndReady();
            // Show Info. of live view (That It'll be available for only 10 minutes)
            // setTimeout(() => {
            //   toast.info(
            //     tCameras("live_feed_info", {
            //       seconds: APP_CONFIG.CAMERA_ADMIN.LIVEVIEW_DURATION_SEC,
            //     })
            //   );
            // }, 3 * 1000);
          }
        }, 10 * 1000); // allowing 10 seconds
      }, 2500); // extra 2.5 seconds
    },
    [modalsState, onCameraAwakeAndReady, tCameras, refetchVehicleRecorderStatus]
  );

  const playVideoButtonHandler = useCallback(() => {
    if (dataVehicleRecorderStatus?.recorder_status === "Unknown") {
      toast.error(t("recorder_status.unknown"));
    } else if (
      dataVehicleRecorderStatus?.recorder_status === "standby" ||
      dataVehicleRecorderStatus?.recorder_status === "offline" ||
      true // always attempt to wake the camera
    ) {
      // open modal
      if (
        thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX &&
        !!dataSingleVehicle?.cameraid &&
        !!dataDeviceListing?.data?.[0]?.cid
      ) {
        // visionmax
        dispatch(
          setModalsData({
            ...modalsState,
            showModalWakeupCamera: {
              cid: dataDeviceListing?.data?.[0]?.cid || "",
              serial_number: dataSingleVehicle.cameraid,
              onSuccessCallback: onCameraWakeupSuccess,
            },
          })
        );
      } else {
        // surfsight
        toast.error(t("recorder_status.standby"));
      }
    } else if (dataVehicleRecorderStatus?.recorder_status === "online") {
      // open modal
      if (
        thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX
      ) {
        // visionmax
        if (!!dataSingleVehicle?.cameraid) {
          onCameraAwakeAndReady();
        } else {
          toast.error(t("camera_serial_number_not_found"));
        }
      } else {
        // surfsight
        // onMediaTokenRetrieved();
        if (APP_CONFIG.DEBUG.ALL) {
          console.log(
            "Play video directly from SD card does not apply to Surfsight cameras."
          );
        }
      }
    } else {
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          `Recorder Status: ${dataVehicleRecorderStatus?.recorder_status}`
        );
      toast.error(t("recorder_status.unknown"));
    }
  }, [
    dataVehicleRecorderStatus,
    dataSingleVehicle,
    dataDeviceListing,
    thisOrgCameraRecorderType,
    thisUserOrganizationId,
    vehicleId,
    modalsState,
    onCameraWakeupSuccess,
    selectedClipTypes,
  ]);

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showVehicleDetails: false }));
    onEventDetailModalClose?.();
  };

  const [drawerOpen, setDrawerOpen] = useState(true);
  useEffect(() => {
    if (!!tripPointData) {
      setDrawerOpen(true);
    }
  }, [tripPointData]);

  const DrawerHandle = useCallback(
    () => (
      <div
        className="absolute bg-white w-8 h-16 shadow-[-5px_0_5px_rgba(0,0,0,0.1)] flex items-center justify-center rounded-tl-lg rounded-bl-lg top-2/4 -left-8 transform -translate-y-8 cursor-pointer"
        onClick={() => setDrawerOpen(!drawerOpen)}
      >
        <img
          src={svgIcons.ChevronRight}
          alt="chevron-right"
          className={`w-6 h-6 ${drawerOpen ? "" : "transform rotate-180"}`}
        />
      </div>
    ),
    [drawerOpen]
  );

  const { updateBuzzerAlert } = useBuzzerAlertActions();
  const [isLoading, setIsLoading] = useState(false);

  const handleMarkResolved = () => {
    if (!buzzerAlertId) return;
    updateBuzzerAlert(
      buzzerAlertId,
      getBuzzerAlertUUID({
        device_id: vehicleId,
        event_info: {
          timestamp: parseInt(buzzerAlertTimestamp || "0"),
        },
      } as Partial<BuzzerAlert>),
      BuzzerAlertStatuses.RESOLVED,
      setIsLoading,
      () => {
        navigate(routeUrls.dashboardChildren.panic_alert);
      }
    );
  };

  if (modalsState.showVehicleDetails === false || !vehicleId || !tripPointData)
    return null;

  const wrapperClass =
    `no-print h-[calc(100vh-10.5rem)] md:h-auto absolute flex flex-col gap-3 px-4 p-4 mx-auto max-w-[70vw] w-[80vw] md:max-w-[calc(100vw-4rem)] md:w-[330px] z-map-popup top-16 right-2 bottom-2 bg-white shadow-2xl rounded-lg Xoverflow-hidden transition` +
    (!!drawerOpen ? "" : " transform translate-x-[calc(100%+0.5em)]");

  if (!tripPointData)
    return (
      <div className={wrapperClass}>
        <DrawerHandle />
        <div className="flex justify-between items-center">
          <div
            className={`absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg z-50`}
          >
            <img
              src={svgIcons.LoadingAnimation}
              alt="Loading animation"
              className="w-12 h-12"
            />
          </div>
        </div>
      </div>
    );

  return (
    <div className={wrapperClass}>
      {/* Drawer Handle */}
      <DrawerHandle />

      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold leading-6 text-heading-black">
          {t("heading")}
        </h2>
        <img
          src={svgIcons.CloseIcon}
          alt="close-icon"
          className="p-1 rounded-full cursor-pointer"
          onClick={() => {
            // setDrawerOpen(false);
            setSelectedTripPoint?.(null);
            hideModal();
          }}
        />
      </div>

      {/* Content */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-base font-semibold leading-4 text-accent-blue-dark">
            {mapVehicleDisplayTitle(vehicleData)}
          </p>
          <p className="text-sm font-medium leading-4 mt-2 text-gray-400">
            {/* {mapVehicleDisplayIds(vehicleData)} */}
            {consistentDisplayAssetItem(
              vehicleData?.deviceid,
              vehicleData?.description,
              vehicleData?.displayname,
              isLoadingAssignedDriver || isErrorAssignedDriver
                ? undefined
                : dataAssignedDriver?.description,
              showDriver
            )}
          </p>
        </div>
      </div>
      <div className="flex gap-4">
        <AdminFormFieldSubmit
          customWrapperClass="flex-1"
          label={t("message")}
          type="button"
          variant="primary"
          disabled={
            !vehicleData?.driverid
              ? true
              : isLoadingAssignedDriver ||
                isErrorAssignedDriver ||
                !dataAssignedDriver ||
                !dataAssignedDriver?.contactphone
          }
          onClick={() =>
            dispatch(setModalsData({ ...modalsState, showSendText: true }))
          }
        />
        <AdminFormFieldSubmit
          customWrapperClass="flex-1"
          label={t("send_location")}
          type="button"
          variant="primary"
          onClick={() =>
            dispatch(setModalsData({ ...modalsState, showSendLocation: true }))
          }
        />
      </div>
      <div className="space-y-6 flex-grow overflow-y-auto">
        {listData?.map((item, index) => (
          <div key={index}>
            <p className="text-xs font-normal leading-5 Xtracking-tighter text-gray-400">
              {item.name}
            </p>
            <p className="text-sm font-medium leading-5 Xtracking-tighter">
              {item.value}
            </p>
            {item.sub && (
              <p className="text-[10px] font-normal leading-5 Xtracking-tighter text-gray-400">
                {item.sub}
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-4">
        <AdminFormFieldSubmit
          customWrapperClass="flex-1"
          label={t("street_view")}
          type="button"
          variant="primary"
          onClick={() => {
            hideModal();
            handleMapTypeChange?.(
              APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
                .type as TMapType
            );
            handleMapTypeChange?.(
              APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.STREETVIEW]
                .type as TMapType,
              !!tripPointData?.latitude && !!tripPointData?.longitude
                ? new google.maps.LatLng(
                    tripPointData?.latitude ?? 0,
                    tripPointData?.longitude ?? 0
                  )
                : undefined
            );
          }}
        />
        <AdminFormFieldSubmit
          customWrapperClass="flex-1"
          label={t("forecast")}
          type="button"
          variant="primary"
          onClick={() => {
            dispatch(
              setModalsData({
                ...modalsState,
                showWeatherForecast: true,
              })
            );
          }}
          disabled={!eventData?.latitude || !eventData?.longitude}
        />
      </div>
      {thisOrgViewVideoAllowed === "1" && (
        <>
          {/* camera type dropdown */}
          {thisOrgCameraRecorderType ===
            ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX &&
            requestVideoButtonState.label === "Request Video" && (
              <AdminFormFieldDropdown
                id="camera-select"
                label={"Camera Type"}
                name={"Select Camera"}
                placeholder={"Select Cameras"}
                value={clipOptions.filter((opt) =>
                  selectedClipTypes.includes(opt.value)
                )}
                isMulti={true}
                onChange={(selectedOptions: any) => {
                  if (selectedOptions) {
                    if (selectedOptions.length <= 4) {
                      const values = selectedOptions.map((o: any) => o.value);
                      setSelectedClipTypes(values);
                    } else {
                      toast.warning("You can select maximum of 4 views.");
                    }
                  } else {
                    setSelectedClipTypes([]);
                  }
                }}
                customWrapperClass={"min-w-32 z-60 mb-2"}
                dimensionClass="h-auto" // Auto height for multiple tags
                options={clipOptions || []}
                key={`camera-select-multi`}
                menuPlacement="top"
              />
            )}
          {/* Request Video Button (creates VisionMax event) */}
          <AdminFormFieldSubmit
            label={
              <>
                {isFetchingRecordingData ||
                isLoadingVideoDetails ||
                isLoadingCameraRequestVideo ||
                isLoadingCameraRequestVideoVM ||
                isLoadingCameraRequestVideoVM ? (
                  <>
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="Loading animation"
                      className="size-5"
                    />
                  </>
                ) : (
                  <>
                    {requestVideoButtonState.label}
                    <img
                      src={svgIcons.VideoCameraIcon}
                      alt="video-camera-icon"
                      className={`size-5 ml-2${
                        requestVideoButtonState.disabled
                          ? " grayscale opacity-50"
                          : ""
                      }`}
                    />
                  </>
                )}
              </>
            }
            type="button"
            variant="primary-like"
            disabled={
              requestVideoButtonState.disabled ||
              isLoadingCameraRequestVideo ||
              isLoadingCameraRequestVideoVM ||
              isLoadingCameraRequestVideoVM ||
              isFetchingRecordingData ||
              isLoadingVideoDetails ||
              selectedClipTypes.length === 0
            }
            onClick={requestVideoButtonState.onClick}
          />
          {/* Play Video Button (plays directly from SD card) */}
          {thisOrgCameraRecorderType ===
            ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX && (
            <AdminFormFieldSubmit
              label={
                selectedClipTypes.length > 1 ? (
                  "Select only 1 Camera Type to Play"
                ) : (
                  <>
                    {t("play_video")}
                    <img
                      src={svgIcons.PlayO}
                      alt="video-camera-icon"
                      className={`size-5 ml-2${
                        isFetchingSingleVehicle ||
                        isFetchingVehicleRecorderStatus ||
                        selectedClipTypes.length > 1 ||
                        selectedClipTypes.length === 0
                          ? " grayscale opacity-50"
                          : ""
                      }`}
                    />
                  </>
                )
              }
              type="button"
              variant="primary-like"
              disabled={
                isFetchingSingleVehicle ||
                isFetchingVehicleRecorderStatus ||
                selectedClipTypes.length > 1 ||
                selectedClipTypes.length === 0
              }
              onClick={playVideoButtonHandler}
            />
          )}
        </>
      )}
      {buzzerAlertTimestamp && buzzerAlertId && (
        <AdminFormFieldSubmit
          type="button"
          variant="success-like"
          label={
            <span className="flex justify-center whitespace-nowrap">
              {tBuzzerAlert("resolved")}
            </span>
          }
          onClick={handleMarkResolved}
          disabled={isLoading}
        />
      )}
      <WeatherForecastModal
        latitude={eventData?.latitude ?? 0}
        longitude={eventData?.longitude ?? 0}
        address={eventData?.address ?? ""}
      />
      <ModalSendText assignedDriver={dataAssignedDriver} />
      <ModalSendLocation
        latitude={eventData?.latitude ?? 0}
        longitude={eventData?.longitude ?? 0}
      />
      {(thisOrgCameraRecorderType !== ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX ||
        (thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX &&
          !!vMCameraEventGetVideoDetailResponse)) && (
        <ModalCameraVideo
          data={dataRecordingDetails}
          {...(!!vMCameraEventGetVideoDetailResponse
            ? {
                vmData: vMCameraEventGetVideoDetailResponse,
                onClose: () => {
                  setVMCameraEventGetVideoDetailResponse(null);
                },
              }
            : {})}
        />
      )}
      {modalsState?.showModalMaintenanceFaultCodes && (
        <ModalMaintenanceFaultCodes
          faultCodeData={vehicleData?.fault_code_info}
          setFaultCodeData={undefined}
        />
      )}
      {thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX && (
        <ModalCameraPlayback
          videoAddress={eventData?.address || "Address not Available"}
          cameraSerialNumber={dataDeviceListing?.data?.[0]?.cid ?? ""}
        />
      )}
    </div>
  );
};

export default React.memo(ModalVehicleTripEventDetails);
